import React, { useState, useEffect, useRef } from "react";

const Welcome: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (containerRef.current) {
        const x = (e.clientX / window.innerWidth) * 2 - 1;
        const y = (e.clientY / window.innerHeight) * 2 - 1;
        
        containerRef.current.style.transform = `perspective(1000px) rotateY(${x * 2}deg) rotateX(${-y * 2}deg)`;
      }
    };

    const handleMouseLeave = () => {
      if (containerRef.current) {
        containerRef.current.style.transform = 'perspective(1000px) rotateY(0deg) rotateX(0deg)';
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  const styles = `
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    .welcome-page {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      background: linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #2c2c2c 100%);
      min-height: 100vh;
      color: #ffffff;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
    }

    /* Animated background elements */
    .background-animation {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      overflow: hidden;
      z-index: 1;
    }

    .floating-element {
      position: absolute;
      width: 4px;
      height: 4px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 50%;
      animation: float 8s infinite linear;
    }

    .floating-element:nth-child(1) { left: 10%; animation-delay: 0s; }
    .floating-element:nth-child(2) { left: 20%; animation-delay: 2s; }
    .floating-element:nth-child(3) { left: 30%; animation-delay: 4s; }
    .floating-element:nth-child(4) { left: 40%; animation-delay: 6s; }
    .floating-element:nth-child(5) { left: 50%; animation-delay: 1s; }
    .floating-element:nth-child(6) { left: 60%; animation-delay: 3s; }
    .floating-element:nth-child(7) { left: 70%; animation-delay: 5s; }
    .floating-element:nth-child(8) { left: 80%; animation-delay: 7s; }
    .floating-element:nth-child(9) { left: 90%; animation-delay: 1.5s; }

    @keyframes float {
      0% {
        transform: translateY(100vh) rotate(0deg);
        opacity: 0;
      }
      10% {
        opacity: 1;
      }
      90% {
        opacity: 1;
      }
      100% {
        transform: translateY(-100px) rotate(360deg);
        opacity: 0;
      }
    }

    /* Grid pattern overlay */
    .grid-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-image: 
        linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px);
      background-size: 60px 60px;
      animation: gridMove 25s linear infinite;
      z-index: 2;
    }

    @keyframes gridMove {
      0% { transform: translate(0, 0); }
      100% { transform: translate(60px, 60px); }
    }

    /* Main welcome container */
    .welcome-container {
      position: relative;
      z-index: 10;
      text-align: center;
      max-width: 800px;
      padding: 60px 40px;
      background: rgba(15, 15, 15, 0.9);
      backdrop-filter: blur(30px);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 24px;
      box-shadow: 0 30px 60px rgba(0, 0, 0, 0.5);
      animation: containerEntrance 1.2s ease-out;
      transition: transform 0.1s ease-out;
    }

    @keyframes containerEntrance {
      0% {
        opacity: 0;
        transform: translateY(50px) scale(0.9);
      }
      100% {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }

    /* Samsung branding */
    .samsung-branding {
      margin-bottom: 40px;
      animation: fadeInDown 1s ease-out 0.3s both;
    }

    .samsung-logo {
      font-size: 42px;
      font-weight: 300;
      letter-spacing: 12px;
      color: #ffffff;
      margin-bottom: 15px;
      position: relative;
      display: inline-block;
    }

    .samsung-logo::after {
      content: '';
      position: absolute;
      bottom: -8px;
      left: 50%;
      transform: translateX(-50%);
      width: 80px;
      height: 2px;
      background: linear-gradient(90deg, transparent, #ffffff, transparent);
      animation: logoGlow 3s ease-in-out infinite;
    }

    @keyframes logoGlow {
      0%, 100% { 
        opacity: 0.4;
        box-shadow: 0 0 10px rgba(255, 255, 255, 0.2);
      }
      50% { 
        opacity: 1;
        box-shadow: 0 0 25px rgba(255, 255, 255, 0.6);
      }
    }

    .system-title {
      font-size: 18px;
      color: #888888;
      font-weight: 300;
      letter-spacing: 3px;
      text-transform: uppercase;
    }

    @keyframes fadeInDown {
      from {
        opacity: 0;
        transform: translateY(-30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    /* Welcome content */
    .welcome-content {
      margin-bottom: 50px;
    }

    .welcome-title {
      font-size: 56px;
      font-weight: 300;
      margin-bottom: 25px;
      background: linear-gradient(135deg, #ffffff 0%, #cccccc 50%, #ffffff 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      animation: fadeInUp 1s ease-out 0.6s both;
      line-height: 1.2;
    }

    .welcome-subtitle {
      font-size: 20px;
      color: #aaaaaa;
      font-weight: 300;
      line-height: 1.6;
      margin-bottom: 15px;
      animation: fadeInUp 1s ease-out 0.9s both;
    }

    .system-description {
      font-size: 16px;
      color: #777777;
      font-weight: 300;
      letter-spacing: 1px;
      animation: fadeInUp 1s ease-out 1.2s both;
    }

    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    /* Enter button */
    .enter-system {
      display: inline-block;
      padding: 18px 40px;
      background: linear-gradient(135deg, #2c2c2c 0%, #404040 100%);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 50px;
      color: #ffffff;
      font-size: 16px;
      font-weight: 500;
      text-decoration: none;
      text-transform: uppercase;
      letter-spacing: 2px;
      cursor: pointer;
      transition: all 0.4s ease;
      position: relative;
      overflow: hidden;
      animation: fadeInUp 1s ease-out 1.5s both;
    }

    .enter-system::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
      transition: left 0.6s ease;
    }

    .enter-system:hover {
      background: linear-gradient(135deg, #404040 0%, #555555 100%);
      transform: translateY(-3px);
      box-shadow: 0 15px 35px rgba(0, 0, 0, 0.4);
      border-color: rgba(255, 255, 255, 0.3);
    }

    .enter-system:hover::before {
      left: 100%;
    }

    .enter-system:active {
      transform: translateY(-1px);
    }

    /* Status indicator */
    .system-status {
      position: absolute;
      top: 30px;
      right: 30px;
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 14px;
      color: #888888;
      animation: fadeIn 1s ease-out 1.8s both;
    }

    .status-dot {
      width: 10px;
      height: 10px;
      background: #00ff88;
      border-radius: 50%;
      animation: pulse 2s infinite;
    }

    @keyframes pulse {
      0%, 100% { 
        opacity: 1; 
        transform: scale(1);
        box-shadow: 0 0 0 0 rgba(0, 255, 136, 0.4);
      }
      50% { 
        opacity: 0.8; 
        transform: scale(1.1);
        box-shadow: 0 0 0 10px rgba(0, 255, 136, 0);
      }
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    /* Decorative elements */
    .welcome-container::before {
      content: '';
      position: absolute;
      top: -2px;
      left: -2px;
      right: -2px;
      bottom: -2px;
      background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.05), transparent);
      border-radius: 26px;
      z-index: -1;
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    .welcome-container:hover::before {
      opacity: 1;
    }

    /* Device counter integration */
    .device-counter {
      display: inline-block;
      font-size: 14px;
      color: #00ff88;
      margin-left: 10px;
      font-weight: 400;
      opacity: 0.8;
    }

    /* Responsive design */
    @media (max-width: 768px) {
      .welcome-container {
        margin: 20px;
        padding: 40px 30px;
      }
      
      .samsung-logo {
        font-size: 32px;
        letter-spacing: 8px;
      }
      
      .welcome-title {
        font-size: 42px;
      }
      
      .welcome-subtitle {
        font-size: 18px;
      }
      
      .system-description {
        font-size: 14px;
      }
      
      .system-status {
        position: static;
        justify-content: center;
        margin-top: 30px;
      }
    }

    @media (max-width: 480px) {
      .welcome-container {
        padding: 30px 20px;
      }
      
      .samsung-logo {
        font-size: 28px;
        letter-spacing: 6px;
      }
      
      .welcome-title {
        font-size: 36px;
      }
      
      .enter-system {
        padding: 15px 30px;
        font-size: 14px;
      }
    }
  `;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      
      <div className="welcome-page">
        <div className="background-animation">
          <div className="floating-element"></div>
          <div className="floating-element"></div>
          <div className="floating-element"></div>
          <div className="floating-element"></div>
          <div className="floating-element"></div>
          <div className="floating-element"></div>
          <div className="floating-element"></div>
          <div className="floating-element"></div>
          <div className="floating-element"></div>
        </div>

        <div className="grid-overlay"></div>

        <div className="welcome-container" ref={containerRef}>


          <div className="samsung-branding">
            <div className="samsung-logo">SAMSUNG</div>
            <div className="system-title">Device Tracker</div>
          </div>

          <div className="welcome-content">
            <h1 className="welcome-title">Bienvenido</h1>
            <p className="welcome-subtitle">
              Sistema de Seguimiento de Dispositivos Samsung
            </p>
            <p className="system-description">
              Monitoreo en tiempo real de dispositivos desde CEDIS hasta tiendas con tecnología GPS
            </p>
          </div>


        </div>
      </div>
    </>
  );
};

export default Welcome;